import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { Wrench, Search, Building2, Package, CheckCircle2, AlertTriangle, Truck, Loader2, Link2, RotateCcw, PackageCheck } from "lucide-react";
import { toast } from "sonner";

const OFICINA_LABEL = {
  llocativo: { txt: "Oficina LLocativo", color: "bg-blue-600", icon: Package },
  maquinas: { txt: "Oficina Máquinas", color: "bg-orange-600", icon: Wrench },
  indefinido: { txt: "Indefinido", color: "bg-zinc-400", icon: AlertTriangle },
};

const STATUS_OFICINA = {
  separado: { txt: "Separado", cls: "bg-emerald-700" },
  completo: { txt: "Completo", cls: "bg-green-600" },
  parcial: { txt: "Parcial", cls: "bg-amber-500" },
  pendente: { txt: "Pendente", cls: "bg-red-600" },
};

function ChipOficina({ tipo }) {
  const cfg = OFICINA_LABEL[tipo] || OFICINA_LABEL.indefinido;
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.color} text-white hover:${cfg.color} gap-1`}>
      <Icon size={11} /> {cfg.txt}
    </Badge>
  );
}

function ModalAssociar({ pedido, onClose, onAssociado }) {
  const [oficina, setOficina] = useState("__todas__");
  const [q, setQ] = useState("");
  const [disponiveis, setDisponiveis] = useState([]);
  const [associados, setAssociados] = useState([]);
  const [selecionados, setSelecionados] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const carregar = useCallback(async () => {
    if (!pedido) return;
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (oficina !== "__todas__") params.oficina_tipo = oficina;
      if (q.trim()) params.q = q.trim();
      const [r1, r2] = await Promise.all([
        api.get("/oficina/associacao/equipamentos-disponiveis", { params }),
        api.get(`/oficina/associacao/pedidos/${pedido.id}/equipamentos`),
      ]);
      setDisponiveis(r1.data.items || []);
      setAssociados(r2.data.items || []);
    } catch (e) { /* nop */ }
    finally { setLoading(false); }
  }, [pedido, oficina, q]);

  useEffect(() => { carregar(); }, [carregar]);

  const toggle = (id) => {
    setSelecionados((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const associar = async () => {
    if (selecionados.size === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/oficina/associacao/pedidos/${pedido.id}/associar`,
        { equipamento_ids: Array.from(selecionados) },
      );
      toast.success(`${data.associados} equipamento(s) reservado(s)`);
      if (data.ignorados?.length) {
        toast.warning(`${data.ignorados.length} ignorado(s)`);
      }
      setSelecionados(new Set());
      onAssociado?.();
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao associar");
    } finally { setBusy(false); }
  };

  const desassociar = async (eqId) => {
    if (!window.confirm("Devolver este equipamento ao estoque?")) return;
    try {
      await api.post(`/oficina/associacao/pedidos/${pedido.id}/desassociar/${eqId}`);
      toast.success("Equipamento devolvido ao estoque");
      onAssociado?.();
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const reclassificar = async (eq, novoTipo) => {
    try {
      await api.patch(`/oficina/associacao/equipamentos/${eq.id}/reclassificar`, { oficina_tipo: novoTipo });
      toast.success(`Reclassificado para ${OFICINA_LABEL[novoTipo].txt}`);
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  return (
    <Dialog open={!!pedido} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="modal-associar">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: "#1B3A6B" }}>
            <Link2 size={18} /> Associar equipamentos · {pedido?.cliente_nome}
          </DialogTitle>
          <DialogDescription>
            Pedido <strong>{pedido?.id?.slice(0, 14)}…</strong> · {pedido?.qtd_solicitada} solicitados · {associados.length} associados
          </DialogDescription>
        </DialogHeader>

        {/* Equipamentos JÁ associados */}
        {associados.length > 0 && (
          <Card className="p-3 bg-green-50 border-green-200">
            <div className="flex items-center gap-2 text-sm font-semibold text-green-900 mb-2">
              <CheckCircle2 size={14} /> Equipamentos reservados ({associados.length})
            </div>
            <div className="space-y-1">
              {associados.map((eq) => (
                <div key={eq.id} className="flex items-center justify-between text-xs bg-white p-2 rounded border">
                  <div className="flex items-center gap-2">
                    <ChipOficina tipo={eq.oficina_inferida} />
                    <span className="font-mono">{eq.serial}</span>
                    <span className="text-muted-foreground">· placa {eq.placa}</span>
                    <span className="text-muted-foreground truncate max-w-[300px]">{eq.observacoes}</span>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => desassociar(eq.id)} data-testid={`desassociar-${eq.id}`}>
                    <RotateCcw size={12} className="mr-1" /> Devolver
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Filtros */}
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <Label className="text-xs uppercase">Oficina</Label>
            <Select value={oficina} onValueChange={setOficina}>
              <SelectTrigger className="w-[200px]" data-testid="filtro-oficina"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__todas__">Todas</SelectItem>
                <SelectItem value="maquinas">Oficina Máquinas</SelectItem>
                <SelectItem value="llocativo">Oficina LLocativo</SelectItem>
                <SelectItem value="indefinido">Indefinido</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs uppercase">Buscar (serial, placa, observação)</Label>
            <div className="relative">
              <Search size={14} className="absolute left-2 top-3 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Ex: EP, transpaleteira, 411…"
                className="pl-7"
                data-testid="busca-equipamento"
              />
            </div>
          </div>
          <Button onClick={associar} disabled={selecionados.size === 0 || busy} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="btn-associar">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Link2 size={14} className="mr-1" />}
            Associar {selecionados.size > 0 && `(${selecionados.size})`}
          </Button>
        </div>

        {/* Tabela disponíveis */}
        <Card>
          {loading ? (
            <div className="p-8 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Carregando…</div>
          ) : disponiveis.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">Nenhum equipamento disponível com esses filtros.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Serial / Placa</TableHead>
                  <TableHead>Observação</TableHead>
                  <TableHead>Propriedade</TableHead>
                  <TableHead>Oficina</TableHead>
                  <TableHead className="text-right">Reclassificar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {disponiveis.map((eq) => (
                  <TableRow key={eq.id} data-testid={`equip-row-${eq.id}`} className={selecionados.has(eq.id) ? "bg-orange-50" : ""}>
                    <TableCell>
                      <Checkbox
                        checked={selecionados.has(eq.id)}
                        onCheckedChange={() => toggle(eq.id)}
                        data-testid={`check-${eq.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      <div>{eq.serial}</div>
                      <div className="text-muted-foreground text-[10px]">placa {eq.placa}</div>
                    </TableCell>
                    <TableCell className="text-xs max-w-[300px] truncate" title={eq.observacoes}>{eq.observacoes || "—"}</TableCell>
                    <TableCell className="text-xs">{eq.propriedade || "—"}</TableCell>
                    <TableCell><ChipOficina tipo={eq.oficina_inferida} /></TableCell>
                    <TableCell className="text-right">
                      <Select value="" onValueChange={(v) => reclassificar(eq, v)}>
                        <SelectTrigger className="w-[140px] h-7 text-xs" data-testid={`reclass-${eq.id}`}><SelectValue placeholder="Mover…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="maquinas">Máquinas</SelectItem>
                          <SelectItem value="llocativo">LLocativo</SelectItem>
                          <SelectItem value="indefinido">Indefinido</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </DialogContent>
    </Dialog>
  );
}

export default function OficinaAssociacao() {
  const { user } = useAuth();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selecionado, setSelecionado] = useState(null);
  const [marcando, setMarcando] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/oficina/associacao/pedidos-aguardando");
      setPedidos(data.items || []);
    } catch (e) {
      toast.error("Erro ao carregar pedidos");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const podeMarcarSeparado = user && ["diretor","supervisao","operador","oficina","gerente"].includes(user.role);

  const marcarSeparado = async (pedido) => {
    if (!window.confirm(`Confirmar separação de ${pedido.equipamentos_associados_count} equipamento(s) do pedido "${pedido.cliente_nome}"?\n\nIsto vai mudar os equipamentos para status "separado" e notificar vendedor + financeiro.`)) return;
    setMarcando(pedido.id);
    try {
      const { data } = await api.post(`/oficina/associacao/pedidos/${pedido.id}/marcar-separado`);
      toast.success(`Pedido separado! ${data.promovidos} equipamento(s) prontos · ${data.notificacoes_enviadas} notificação(ões) enviada(s)`);
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao marcar como separado");
    } finally { setMarcando(null); }
  };

  return (
    <div className="space-y-5" data-testid="oficina-associacao-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Wrench size={28} style={{ color: "#1B3A6B" }} />
          <div>
            <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>Associação de Equipamentos</h1>
            <p className="text-sm text-muted-foreground">Direcione equipamentos do estoque aos pedidos fechados. Oficina LLocativo · Oficina Máquinas.</p>
          </div>
        </div>
        <Button variant="outline" onClick={carregar} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Truck size={14} className="mr-1" />}
          Atualizar
        </Button>
      </div>

      {loading ? (
        <Card className="p-10 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Carregando pedidos…</Card>
      ) : pedidos.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">
          Nenhum pedido aguardando associação.
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {pedidos.map((p) => {
            const st = STATUS_OFICINA[p.status_oficina] || STATUS_OFICINA.pendente;
            return (
              <Card key={p.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`pedido-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground font-mono">{p.id.slice(0, 18)}…</div>
                    <h3 className="font-bold text-base truncate" style={{ color: "#1B3A6B" }}>{p.cliente_nome}</h3>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5 flex-wrap">
                      <Badge variant="outline" className="gap-1 text-[10px]"><Building2 size={10} />{p.unidade_id || "—"}</Badge>
                      <span>· {p.vendedor_nome}</span>
                      <span>· {p.tipo_contrato}</span>
                    </div>
                  </div>
                  <Badge className={`${st.cls} text-white hover:${st.cls}`}>{st.txt}</Badge>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
                  <div className="bg-zinc-50 rounded p-2">
                    <div className="text-muted-foreground uppercase text-[10px]">Solicitados</div>
                    <div className="text-lg font-bold" style={{ color: "#1B3A6B" }}>{p.qtd_solicitada}</div>
                  </div>
                  <div className="bg-zinc-50 rounded p-2">
                    <div className="text-muted-foreground uppercase text-[10px]">Associados</div>
                    <div className="text-lg font-bold text-green-700">{p.equipamentos_associados_count}</div>
                  </div>
                </div>

                <div className="text-xs text-muted-foreground mb-3">
                  <strong>Itens:</strong>
                  <ul className="list-disc list-inside mt-1">
                    {(p.itens || []).map((it, i) => (
                      <li key={i}>{it.quantidade}× {it.nome}</li>
                    ))}
                  </ul>
                </div>

                <Button
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                  onClick={() => setSelecionado(p)}
                  data-testid={`abrir-${p.id}`}
                  disabled={p.status_oficina === "separado"}
                >
                  <Link2 size={14} className="mr-1" /> {p.status_oficina === "separado" ? "Já separado" : "Associar equipamentos"}
                </Button>

                {/* Botão Marcar como Separado — só quando completo e usuário autorizado */}
                {p.status_oficina === "completo" && podeMarcarSeparado && (
                  <Button
                    className="w-full mt-2 bg-emerald-700 hover:bg-emerald-800 text-white"
                    onClick={() => marcarSeparado(p)}
                    disabled={marcando === p.id}
                    data-testid={`marcar-separado-${p.id}`}
                  >
                    {marcando === p.id ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : (
                      <PackageCheck size={14} className="mr-1" />
                    )}
                    Marcar como Separado
                  </Button>
                )}

                {p.status_oficina === "separado" && p.data_separacao && (
                  <div className="mt-2 text-[11px] text-emerald-700 flex items-center gap-1 bg-emerald-50 px-2 py-1.5 rounded">
                    <CheckCircle2 size={12} />
                    Separado em {new Date(p.data_separacao).toLocaleString("pt-BR")} por {p.usuario_separacao}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <ModalAssociar pedido={selecionado} onClose={() => setSelecionado(null)} onAssociado={carregar} />
    </div>
  );
}
